import React, { useState, useEffect, useMemo } from "react";

/* ---------- Reusable bits ---------- */
const Divider = () => <div className="h-[2px] w-full bg-egypt-gold/50" />;

const API_URL = process.env.REACT_APP_API_URL;

const Tooltip = ({ text }) => (
  <span className="ml-2 text-xs text-egypt-cream/80 cursor-help relative group align-middle">
    ℹ️
    <span className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-56 text-center text-sm text-egypt-black bg-egypt-cream px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition z-40 shadow-sm">
      {text}
    </span>
  </span>
);

/* ---------- Editable item ---------- */
function EditableItem({ text, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text);

  const handleSave = () => {
    const v = value.trim();
    if (v) onEdit(v);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between bg-egypt-blackSoft/40 p-2 rounded-md">
      {editing ? (
        <input
          className="flex-1 bg-transparent border-b border-egypt-gold px-2 py-1 focus:outline-none"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          autoFocus
        />
      ) : (
        <span className="flex-1">{text}</span>
      )}
      <div className="flex gap-2 ml-3 text-egypt-gold">
        <button onClick={() => setEditing((p) => !p)} title="Edit" className="hover:text-egypt-cream transition">✏️</button>
        <button onClick={onDelete} title="Delete" className="hover:text-red-400 transition">🗑️</button>
      </div>
    </div>
  );
}

export default function DecisionForm() {
  const [goal, setGoal] = useState("");
  const [criteria, setCriteria] = useState([]);
  const [subcriteria, setSubcriteria] = useState({});
  const [alternatives, setAlternatives] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [results, setResults] = useState(null);

  const [newCriterion, setNewCriterion] = useState("");
  const [newAlternative, setNewAlternative] = useState("");
  const [subcriterionInputs, setSubcriterionInputs] = useState({});

  /* ---------- Track if user started typing (for theme) ---------- */
  const [hasInput, setHasInput] = useState(false);
  useEffect(() => {
    const nonEmpty =
      goal.trim() ||
      alternatives.length > 0 ||
      criteria.length > 0 ||
      Object.keys(subcriteria).some((k) => (subcriteria[k] || []).length > 0);
    setHasInput(!!nonEmpty);
  }, [goal, criteria, subcriteria, alternatives]);

  /* ---------- Load/Save localStorage ---------- */
  useEffect(() => {
    const saved = localStorage.getItem("decisionModel");
    if (saved) {
      try {
        const m = JSON.parse(saved);
        setGoal(m.goal || "");
        setCriteria(m.criteria || []);
        setSubcriteria(m.subcriteria || {});
        setAlternatives(m.alternatives || []);
        setComparisons(m.comparisons || []);
        setResults(m.results || null);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const model = { goal, criteria, subcriteria, alternatives, comparisons, results };
    localStorage.setItem("decisionModel", JSON.stringify(model));
  }, [goal, criteria, subcriteria, alternatives, comparisons, results]);

  /* ---------- Add helpers (Enter + Button) ---------- */
  const addCriterion = () => {
    const name = newCriterion.trim();
    if (!name) return;
    setCriteria((prev) => [...prev, name]);
    setNewCriterion("");
  };

  const addAlternative = () => {
    const name = newAlternative.trim();
    if (!name) return;
    setAlternatives((prev) => [...prev, name]);
    setNewAlternative("");
  };

  const addSubcriterion = (crit) => {
    const val = (subcriterionInputs[crit] || "").trim();
    if (!val) return;
    setSubcriteria((prev) => ({ ...prev, [crit]: [...(prev[crit] || []), val] }));
    setSubcriterionInputs((prev) => ({ ...prev, [crit]: "" }));
  };

  /* ---------- Edit/Delete ---------- */
  const editCriterion = (i, val) =>
    setCriteria((prev) => prev.map((c, idx) => (idx === i ? val : c)));
  const deleteCriterion = (i) =>
    setCriteria((prev) => prev.filter((_, idx) => idx !== i));

  const editAlternative = (i, val) =>
    setAlternatives((prev) => prev.map((a, idx) => (idx === i ? val : a)));
  const deleteAlternative = (i) =>
    setAlternatives((prev) => prev.filter((_, idx) => idx !== i));

  const editSubcriterion = (crit, j, val) =>
    setSubcriteria((prev) => ({
      ...prev,
      [crit]: prev[crit].map((s, idx) => (idx === j ? val : s)),
    }));
  const deleteSubcriterion = (crit, j) =>
    setSubcriteria((prev) => ({
      ...prev,
      [crit]: prev[crit].filter((_, idx) => idx !== j),
    }));

  /* ---------- Backend calls ---------- */
  const generatePairs = async () => {
    const payload = { goal, criteria, subcriteria, alternatives };
    try {
      const res = await fetch(`${API_URL}/generate_pairs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to fetch pairs");
      const data = await res.json();
      const initPairs = (data.pairs || []).map((p) => ({ ...p, value: 1 }));
      setComparisons(initPairs);
      setResults(null);
    } catch (e) {
      console.error("Error generating pairs:", e);
      alert("Couldn’t start comparison. Please try again.");
    }
  };

  const handleSubmit = async () => {
    const payload = { goal, criteria, subcriteria, alternatives, comparisons };
    try {
      const res = await fetch(`${API_URL}/ahp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to run AHP");
      const data = await res.json();
      setResults(data);
    } catch (e) {
      console.error("Error running AHP:", e);
      alert("Something went wrong while calculating your results.");
    }
  };

  const resetForm = () => {
    if (window.confirm("Clear everything? This will remove your saved model.")) {
      localStorage.removeItem("decisionModel");
      setGoal("");
      setCriteria([]);
      setSubcriteria({});
      setAlternatives([]);
      setComparisons([]);
      setResults(null);
      setNewCriterion("");
      setNewAlternative("");
      setSubcriterionInputs({});
    }
  };

  const updateComparisonValue = (index, value) => {
    setComparisons((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], value };
      return copy;
    });
  };

  const topRecommendation = useMemo(
    () => (results?.results?.length ? results.results[0][0] : null),
    [results]
  );

  /* ---------- Friendlier scale ---------- */
  const verbalScale = [
    { step: -4, label: "Much better", val: 1 / 9 },
    { step: -3, label: "Clearly better", val: 1 / 7 },
    { step: -2, label: "Better", val: 1 / 5 },
    { step: -1, label: "A bit better", val: 1 / 3 },
    { step: 0, label: "About the same", val: 1 },
    { step: 1, label: "A bit better", val: 3 },
    { step: 2, label: "Better", val: 5 },
    { step: 3, label: "Clearly better", val: 7 },
    { step: 4, label: "Much better", val: 9 },
  ];
  const stepToValue = (s) => verbalScale.find((x) => x.step === s).val;
  const valueToStep = (v) => (verbalScale.find((x) => x.val === v)?.step ?? 0);
  const stepToLabel = (s) => verbalScale.find((x) => x.step === s).label;

  /* ---------- Shared style helpers ---------- */
  const titleClass = `text-2xl font-heading mb-3 flex items-center transition-colors duration-500 ${
    hasInput ? "text-egypt-black" : "text-egypt-gold"
  }`;
  const primaryBtnClass = `px-6 py-2 rounded-lg font-bold transition-colors duration-500 ${
    hasInput
      ? "bg-egypt-black text-egypt-cream hover:bg-egypt-gold hover:text-egypt-black"
      : "bg-egypt-gold text-black hover:bg-egypt-black hover:text-egypt-gold"
  }`;
  const smallBtnClass = `px-4 py-2 rounded-lg font-semibold transition-colors duration-500 ${
    hasInput
      ? "bg-egypt-black text-egypt-cream hover:bg-egypt-gold hover:text-egypt-black"
      : "bg-egypt-gold text-black hover:bg-egypt-black hover:text-egypt-gold"
  }`;
  const mutedTextClass = hasInput ? "text-egypt-black/70" : "text-egypt-cream/80";
  const subListTextClass = hasInput ? "text-egypt-black/80" : "text-egypt-cream/90";
  const placeholderClass = hasInput
    ? "placeholder-egypt-black/40"
    : "placeholder-egypt-offwhite/60";

  /* ---------- UI ---------- */
  return (
    <div
      className={`w-full max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-20 transition-colors duration-700 ${
        hasInput
          ? "bg-papyrus-light text-egypt-black" // AFTER typing: light papyrus
          : "bg-transparent" // CHANGED: BEFORE typing, let the global page background show through
      }`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10">

        {/* LEFT CONTENT */}
        <div
          className={`space-y-16 transition-colors duration-700 ${
            hasInput ? "text-egypt-black" : "" // CHANGED: don't force text color before typing; inherit site defaults
          }`}
        >
          {/* Goal */}
          <section>
            <h2 className={titleClass}>
              What do you want to decide?
              <Tooltip text="Describe what you're trying to choose or plan." />
            </h2>
            <input
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder='e.g. "Where should I go for a trip?"'
              className={`w-full px-2 py-2 bg-transparent border-b-2 border-egypt-gold focus:outline-none focus:border-egypt-cream ${placeholderClass}`}
            />
          </section>

          <Divider />

          {/* Options */}
          <section>
            <h2 className={titleClass}>
              What are your options?
              <Tooltip text="List the choices you’re deciding between." />
            </h2>

            {alternatives.length > 0 ? (
              <ul className="space-y-2 mb-3">
                {alternatives.map((a, i) => (
                  <li key={i}>
                    <EditableItem
                      text={a}
                      onEdit={(val) => editAlternative(i, val)}
                      onDelete={() => deleteAlternative(i)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`${mutedTextClass} italic mb-3`}>No options added yet</p>
            )}

            <div className="flex items-center gap-2">
              <input
                value={newAlternative}
                onChange={(e) => setNewAlternative(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addAlternative()}
                placeholder="Type a new option"
                className={`flex-1 px-3 py-2 bg-transparent border-b-2 border-egypt-gold focus:outline-none focus:border-egypt-cream ${placeholderClass}`}
              />
              <button onClick={addAlternative} className={smallBtnClass}>
                + Add
              </button>
            </div>
          </section>

          <Divider />

          {/* Factors */}
          <section>
            <h2 className={titleClass}>
              What matters to you?
              <Tooltip text="Things that affect your decision (like cost, comfort, or time)." />
            </h2>

            {criteria.length > 0 ? (
              <ul className="space-y-4 mb-3">
                {criteria.map((c, i) => (
                  <li key={i}>
                    <EditableItem
                      text={c}
                      onEdit={(val) => editCriterion(i, val)}
                      onDelete={() => deleteCriterion(i)}
                    />

                    {/* subcriteria list */}
                    {subcriteria[c]?.length > 0 && (
                      <ul className={`ml-4 mt-2 list-disc ${subListTextClass}`}>
                        {subcriteria[c].map((s, j) => (
                          <li key={j} className="ml-2">
                            <EditableItem
                              text={s}
                              onEdit={(val) => editSubcriterion(c, j, val)}
                              onDelete={() => deleteSubcriterion(c, j)}
                            />
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* add subcriterion (Enter + Button) */}
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        value={subcriterionInputs[c] || ""}
                        onChange={(e) =>
                          setSubcriterionInputs((prev) => ({ ...prev, [c]: e.target.value }))
                        }
                        onKeyDown={(e) => e.key === "Enter" && addSubcriterion(c)}
                        placeholder={`Add detail under ${c}`}
                        className={`flex-1 px-3 py-2 bg-transparent border-b-2 border-egypt-gold focus:outline-none focus:border-egypt-cream ${placeholderClass}`}
                      />
                      <button onClick={() => addSubcriterion(c)} className={smallBtnClass}>
                        + Add
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`${mutedTextClass} italic mb-3`}>No factors added yet</p>
            )}

            <div className="flex items-center gap-2">
              <input
                value={newCriterion}
                onChange={(e) => setNewCriterion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCriterion()}
                placeholder="Type what matters"
                className={`flex-1 px-3 py-2 bg-transparent border-b-2 border-egypt-gold focus:outline-none focus:border-egypt-cream ${placeholderClass}`}
              />
              <button onClick={addCriterion} className={smallBtnClass}>
                + Add
              </button>
            </div>
          </section>

          <Divider />

          {/* Actions */}
          <div className="flex flex-wrap gap-4">
            <button onClick={generatePairs} className={primaryBtnClass}>
              Start Comparing
            </button>
            <button
              onClick={resetForm}
              className="px-6 py-2 rounded-lg font-bold bg-red-500 text-white hover:scale-105 transition"
            >
              Reset
            </button>
          </div>

          {/* Comparisons */}
          {comparisons.length > 0 && (
            <section className="mt-10">
              <h2 className={titleClass}>Compare Your Options</h2>
              {comparisons.map((cmp, i) => {
                const step = valueToStep(cmp.value);
                return (
                  <div key={i} className="mb-8">
                    <div className="mb-2">
                      When it comes to <b>{cmp.parent || "your decision"}</b>, which one do you prefer?
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-28 text-right">{cmp.item1}</span>
                      <input
                        type="range"
                        min="-4"
                        max="4"
                        step="1"
                        value={step}
                        onChange={(e) =>
                          updateComparisonValue(i, stepToValue(parseInt(e.target.value)))
                        }
                        className="flex-1 accent-egypt-gold"
                      />
                      <span className="w-28">{cmp.item2}</span>
                    </div>
                    <div className={`mt-2 text-sm text-center ${mutedTextClass}`}>
                      {step === 0
                        ? "They feel about the same"
                        : step > 0
                        ? `${cmp.item1} seems ${stepToLabel(step)}`
                        : `${cmp.item2} seems ${stepToLabel(step)}`}
                    </div>
                  </div>
                );
              })}
              <button onClick={handleSubmit} className={primaryBtnClass + " mt-6"}>
                Show My Best Option
              </button>
            </section>
          )}

          {/* Results */}
          {results?.results && (
            <section id="results" className="text-center mt-10">
              <h2 className={titleClass}>Your Decision Result</h2>
              <div className="flex flex-wrap justify-center gap-6 mb-6">
                {results.results.map(([option, score], i) => (
                  <div
                    key={i}
                    className={`w-40 h-40 flex flex-col items-center justify-center rounded-full border-4 border-egypt-gold bg-egypt-black/50 text-lg font-bold ${
                      i === 0 ? "text-egypt-gold scale-110" : hasInput ? "text-egypt-black" : "text-egypt-cream/90"
                    }`}
                  >
                    <span>{option}</span>
                    <span className="text-2xl">{(score * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              {topRecommendation && (
                <div className={`mt-4 text-xl font-bold ${hasInput ? "text-egypt-black" : "text-egypt-cream"}`}>
                  ✅ Based on your answers, we suggest{" "}
                  <span className="text-egypt-gold">{topRecommendation}</span>
                </div>
              )}

              {/* Consistency — simplified copy */}
              {results?.consistency && typeof results.consistency.criteria_CR === "number" && (
                <div className="mt-6">
                  {results.consistency.criteria_CR > 0.1 ? (
                    <p className="text-red-400">
                      ⚠️ Your comparisons are a bit inconsistent (CR ={" "}
                      {(results.consistency.criteria_CR * 100).toFixed(2)}%). Review them if you’d
                      like a more accurate decision.
                    </p>
                  ) : (
                    <p className="text-green-400">
                      ✅ Looks consistent (CR ={" "}
                      {(results.consistency.criteria_CR * 100).toFixed(2)}%). Your sliders tell a
                      clear story.
                    </p>
                  )}
                </div>
              )}
            </section>
          )}
        </div>

        {/* RIGHT SUMMARY (kept dark as requested) */}
        <aside className="hidden lg:block sticky top-24 self-start text-egypt-cream/80 bg-egypt-blackSoft/60 rounded-2xl p-6 shadow-lg border-2 border-egypt-gold">
          <h3 className="text-lg font-bold text-egypt-gold mb-3">Summary</h3>
          {goal && <p className="mb-2">🎯 <b>Goal:</b> {goal}</p>}
          {alternatives.length > 0 && (
            <p className="mb-2">✨ <b>Options:</b> {alternatives.join(", ")}</p>
          )}
          {criteria.length > 0 && (
            <div className="mb-2">
              ⚖️ <b>What matters:</b>
              <ul className="list-disc ml-6 mt-1">
                {criteria.map((c) => (
                  <li key={c}>
                    {c}
                    {subcriteria[c]?.length > 0 && (
                      <ul className="list-circle ml-5 text-sm text-egypt-cream/70">
                        {subcriteria[c].map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {results?.results && (
            <div className="mt-4">
              <h4 className="font-semibold text-egypt-gold">Best Match</h4>
              <p className="text-xl">{results.results[0][0]}</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
